import React from 'react';
import { X } from 'lucide-react';
import type { MarginBasisOption, MarginBreakdownDetail as Detail } from './types';
import { formatEuro, formatInBasis } from './format';

export interface MarginBreakdownDetailProps {
  detail: Detail | null | undefined;
  /** The basis the table is read in — the figure repeats it, the euro total sits beside it. */
  basisOption: MarginBasisOption | undefined;
  locale?: string;
  emptyText: string;
  onClose?: () => void;
}

/**
 * The Rechenweg of one row: the figure in the selected basis, the euro total, the
 * formula, the source lines with their sum, and one note. A row without a figure
 * shows its `pending` sentence and nothing else — never a fabricated 0,00 €.
 */
export const MarginBreakdownDetail: React.FC<MarginBreakdownDetailProps> = ({
  detail,
  basisOption,
  locale = 'de-DE',
  emptyText,
  onClose,
}) => {
  if (!detail) {
    return <p className="margin-breakdown__empty">{emptyText}</p>;
  }
  const inBasis = formatInBasis(detail.value, basisOption, locale);
  const isTotalBasis = basisOption?.divisor === 1 && !basisOption.unit;
  const sum = (detail.lines ?? []).reduce((acc, line) => acc + line.amount, 0);

  return (
    <div className="margin-breakdown__detail">
      <div className="margin-breakdown__detail-head">
        <h3 className="margin-breakdown__detail-title">{detail.title}</h3>
        {onClose && (
          <button type="button" className="margin-breakdown__close" aria-label="Rechenweg schließen" onClick={onClose}>
            <X size={18} aria-hidden="true" />
          </button>
        )}
      </div>
      {detail.value === null ? (
        <p className="margin-breakdown__empty">{detail.pending ?? 'Noch keine Zeile modelliert.'}</p>
      ) : (
        <>
          <div className="margin-breakdown__figure">
            <span className="margin-breakdown__figure-big">{inBasis ?? formatEuro(detail.value, locale)}</span>
            {!isTotalBasis && inBasis && (
              <span className="margin-breakdown__figure-alt">{formatEuro(detail.value, locale)}</span>
            )}
          </div>
          {/* div, not p: the formula may be a factor TABLE with links (HOF-467), which a <p> cannot contain. */}
          {detail.formula && <div className="margin-breakdown__formula">{detail.formula}</div>}
          {detail.warning && (
            <div className="margin-breakdown__callout" role="note">
              {detail.warning}
            </div>
          )}
          {detail.lines && detail.lines.length > 0 && (
            <table className="margin-breakdown__lines" aria-label="Belege">
              <tbody>
                {detail.lines.map((line, i) => (
                  <tr key={i}>
                    <td className="margin-breakdown__lines-period">{line.period}</td>
                    <td>{line.text}</td>
                    <td className="margin-breakdown__lines-amount">{formatEuro(line.amount, locale)}</td>
                  </tr>
                ))}
                <tr className="margin-breakdown__lines-total">
                  <td />
                  <td>Summe</td>
                  <td className="margin-breakdown__lines-amount">{formatEuro(sum, locale)}</td>
                </tr>
              </tbody>
            </table>
          )}
          {detail.note && <div className="margin-breakdown__note">{detail.note}</div>}
        </>
      )}
    </div>
  );
};
